import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Division, SPL_DIVISIONS, PADEL_EAGLE_DIVISIONS } from "../types";
import { useOrganization } from "./OrganizationContext";
import { useSeason } from "./SeasonContext";

interface DivisionContextType {
  currentDivision: Division;
  setCurrentDivision: (division: Division) => void;
  divisions: Division[];          // Season-filtered list
  allDivisions: Division[];       // Union across all seasons + org config
  setPlayerDivision: (division: Division | null) => void;
}

const DivisionContext = createContext<DivisionContextType | undefined>(
  undefined,
);

interface DivisionProviderProps {
  children: ReactNode;
}

export function DivisionProvider({ children }: DivisionProviderProps) {
  const { currentOrg } = useOrganization();
  const { selectedSeason, allSeasons } = useSeason();

  // Get org-specific divisions based on organization slug
  const orgDivisions = React.useMemo(() => {
    if (currentOrg?.divisions && currentOrg.divisions.length > 0) {
      const configured = currentOrg.divisions as Division[];
      // Padel Eagle: Beginner -> Intermediate -> Advanced -> Open (others after), "All" (Show All) last
      if (currentOrg.slug === "padel-eagle") {
        const order = ["Beginner", "Intermediate", "Advanced", "Open"];
        const rank = (d: string) => (order.includes(d) ? order.indexOf(d) : d === "All" ? 99 : order.length);
        const withAll = configured.includes("All" as Division) ? configured : ([...configured, "All"] as Division[]);
        return [...withAll].sort((a, b) => rank(a) - rank(b));
      }
      return configured;
    }
    // Fallback based on org slug
    const isPadelEagle = currentOrg?.slug === "padel-eagle";
    return isPadelEagle ? PADEL_EAGLE_DIVISIONS : SPL_DIVISIONS;
  }, [currentOrg]);

  // Union of org divisions + every season's divisions — never shrinks
  const allDivisions = React.useMemo(() => {
    const set = new Set<Division>(orgDivisions);
    allSeasons.forEach((s) => {
      (s.divisions || []).forEach((d) => set.add(d as Division));
    });
    return Array.from(set);
  }, [orgDivisions, allSeasons]);

  // When a season is selected and it has a divisions list, restrict to those divisions.
  // Otherwise fall back to the org-wide list.
  const availableDivisions = React.useMemo(() => {
    if (selectedSeason?.divisions && selectedSeason.divisions.length > 0) {
      // Keep only divisions that are both in the season and in the org list,
      // but always preserve "unassigned", "All" and "Test" from the org list.
      const seasonSet = new Set(selectedSeason.divisions);
      return orgDivisions.filter(
        (d) => seasonSet.has(d) || d === "unassigned" || d === "All" || d === "Test"
      );
    }
    return orgDivisions;
  }, [selectedSeason, orgDivisions]);

  // Initialize with saved division or default - must handle orgDivisions not being available yet
  const [currentDivision, setCurrentDivisionState] = useState<Division>(() => {
    const saved = localStorage.getItem("padelLeague_selectedDivision");
    console.log("[DivisionContext] Initializing - saved division:", saved);

    if (saved) {
      console.log("[DivisionContext] Restoring saved division:", saved);
      return saved as Division;
    }
    // Default to first division in SPL list (will update when org loads)
    console.log("[DivisionContext] Using default division: Division 1");
    return "Division 1";
  });

  // Reset current division only if it doesn't exist anywhere (not in any season or org config).
  // If it merely isn't in the current season's filtered list, keep it – the admin may have
  // intentionally selected a division from a previous season.
  React.useEffect(() => {
    if (!allDivisions.includes(currentDivision)) {
      // Padel Eagle defaults to showing everything
      const newDefault =
        currentOrg?.slug === "padel-eagle" && availableDivisions.includes("All" as Division)
          ? ("All" as Division)
          : availableDivisions.find(d => d !== "All") || availableDivisions[0];
      console.log("[DivisionContext] Division no longer valid anywhere, resetting to:", newDefault);
      setCurrentDivisionState(newDefault);
    }
  }, [allDivisions, availableDivisions, currentDivision, currentOrg]);

  // Function to set player's division from outside (called by AuthContext)
  const setPlayerDivision = (division: Division | null) => {
    if (division && availableDivisions.includes(division as Division)) {
      const saved = localStorage.getItem("padelLeague_selectedDivision");
      // Only auto-switch if no explicit selection was made
      if (!saved) {
        console.log(
          "[DivisionContext] Setting to player's division:",
          division,
        );
        setCurrentDivisionState(division);
      }
    }
  };

  // Save to localStorage whenever division changes
  const setCurrentDivision = (division: Division) => {
    console.log(
      "[DivisionContext] Setting division from",
      currentDivision,
      "to",
      division,
    );
    setCurrentDivisionState(division);
    localStorage.setItem("padelLeague_selectedDivision", division);
  };

  const value: DivisionContextType = {
    currentDivision,
    setCurrentDivision,
    divisions: availableDivisions, // Season-filtered or org-wide divisions
    allDivisions,                  // Full union — for admin screens
    setPlayerDivision,
  };

  return (
    <DivisionContext.Provider value={value}>
      {children}
    </DivisionContext.Provider>
  );
}

export function useDivision() {
  const context = useContext(DivisionContext);
  if (context === undefined) {
    throw new Error("useDivision must be used within a DivisionProvider");
  }
  return context;
}

export { DivisionContext };
